import clsx from 'clsx';
import { ReactNode } from 'react';

export type DraggedItemType = {
  id: string;
  label: string;
  type: string;
} | null;

type ColorScheme =
  | 'blue'
  | 'white'
  | 'yellow'
  | 'cyan'
  | 'amber'
  | 'orange'
  | 'purple'
  | 'green'
  | 'custom';

type DragOverlayItemProps = {
  width: number;
  height: number;
  colorScheme: ColorScheme;
  children: ReactNode;
  customColors?: {
    bg: string;
    border: string;
  };
};

const colorClasses: Record<Exclude<ColorScheme, 'custom'>, string> = {
  blue: 'border-blue-500 bg-blue-100',
  white: 'border-gray-400 bg-white',
  yellow: 'border-yellow-500 bg-yellow-100',
  cyan: 'border-cyan-600 bg-cyan-400',
  amber: 'border-amber-700 bg-amber-500',
  orange: 'border-orange-500 bg-orange-100',
  purple: 'border-purple-500 bg-purple-100',
  green: 'border-green-500 bg-green-100'
};

export default function DragOverlayItem({
  width,
  height,
  colorScheme,
  children,
  customColors
}: DragOverlayItemProps) {
  return (
    <div
      className={clsx(
        'rounded border-2 shadow-lg',
        colorScheme !== 'custom' && colorClasses[colorScheme]
      )}
      style={{
        width,
        height,
        opacity: 0.8,
        cursor: 'grabbing',
        ...(colorScheme === 'custom' && customColors
          ? {
              backgroundColor: customColors.bg,
              borderColor: customColors.border
            }
          : {})
      }}
    >
      {children}
    </div>
  );
}

type LabeledDragOverlayItemProps = {
  width: number;
  height: number;
  colorScheme: Exclude<ColorScheme, 'custom'>;
  label: string;
  sublabel?: string;
  zoom?: number;
};

const textColorClasses: Record<Exclude<ColorScheme, 'custom'>, string> = {
  blue: 'text-blue-900',
  white: 'text-gray-900',
  yellow: 'text-yellow-900',
  cyan: 'text-cyan-900',
  amber: 'text-amber-900',
  orange: 'text-orange-900',
  purple: 'text-purple-900',
  green: 'text-green-900'
};

const sublabelColorClasses: Record<Exclude<ColorScheme, 'custom'>, string> = {
  blue: 'text-blue-700',
  white: 'text-gray-600',
  yellow: 'text-yellow-700',
  cyan: 'text-cyan-700',
  amber: 'text-amber-700',
  orange: 'text-orange-700',
  purple: 'text-purple-700',
  green: 'text-green-700'
};

export function LabeledDragOverlayItem({
  width,
  height,
  colorScheme,
  label,
  sublabel,
  zoom = 1
}: LabeledDragOverlayItemProps) {
  return (
    <DragOverlayItem width={width} height={height} colorScheme={colorScheme}>
      <div
        className={clsx(
          'flex h-full w-full flex-col items-center justify-center text-center font-medium',
          textColorClasses[colorScheme]
        )}
        style={{ fontSize: `${0.875 * zoom}rem` }}
      >
        <div className='leading-tight'>{label}</div>
        {sublabel && (
          <div
            className={clsx(sublabelColorClasses[colorScheme])}
            style={{ fontSize: `${0.75 * zoom}rem` }}
          >
            {sublabel}
          </div>
        )}
      </div>
    </DragOverlayItem>
  );
}

type IconDragOverlayItemProps = {
  width: number;
  height: number;
  colorScheme: Exclude<ColorScheme, 'custom'>;
  icon: ReactNode;
};

export function IconDragOverlayItem({
  width,
  height,
  colorScheme,
  icon
}: IconDragOverlayItemProps) {
  return (
    <DragOverlayItem width={width} height={height} colorScheme={colorScheme}>
      <div className='flex h-full w-full items-center justify-center'>
        {icon}
      </div>
    </DragOverlayItem>
  );
}

type CompactLabelDragOverlayItemProps = {
  width: number;
  height: number;
  colorScheme: Exclude<ColorScheme, 'custom'>;
  label: string;
};

export function CompactLabelDragOverlayItem({
  width,
  height,
  colorScheme,
  label
}: CompactLabelDragOverlayItemProps) {
  return (
    <DragOverlayItem width={width} height={height} colorScheme={colorScheme}>
      <div
        className={clsx(
          'flex h-full w-full items-center justify-center p-2 text-sm font-medium',
          textColorClasses[colorScheme]
        )}
      >
        <div>{label}</div>
      </div>
    </DragOverlayItem>
  );
}
